/**
 * CRM Shell — shared `fetchAuth` helper.
 *
 * Wraps native fetch with auto-refresh-on-401 logic так же как apiClient.js
 * для калькулятора. Все CRM-shell sections должны использовать fetchAuth
 * вместо raw fetch чтобы выживать после истечения 30-минутного access_token.
 *
 * Поведение:
 *   1. Делает запрос с credentials:'include' (httpOnly cookies автоматом)
 *   2. Если 401 NO_TOKEN или TOKEN_EXPIRED → POST /api/v1/auth/refresh
 *   3. Если refresh ok → retry оригинальный запрос (один раз)
 *   4. Если refresh fail → location.replace('/login.html')
 *
 * Защита от storm: параллельные refresh-запросы шарят один inflight Promise.
 *
 * @module crm-shell/api
 */

let _refreshInFlight = null;

async function _refresh() {
    if (_refreshInFlight) return _refreshInFlight;
    _refreshInFlight = (async () => {
        try {
            const r = await fetch('/api/v1/auth/refresh', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            if (!r.ok) return false;
            const d = await r.json().catch(() => null);
            return !!(d && d.success);
        } catch (_) {
            return false;
        }
    })();
    try {
        return await _refreshInFlight;
    } finally {
        _refreshInFlight = null;
    }
}

/**
 * Fetch wrapper с auto-refresh on 401.
 * @param {string} url
 * @param {RequestInit} init
 * @returns {Promise<Response>}
 */
export async function fetchAuth(url, init = {}) {
    const opts = { credentials: 'include', ...init };
    let res = await fetch(url, opts);
    if (res.status !== 401) return res;

    // Парсим body чтобы понять, стоит ли пытаться refresh
    let code = null;
    try {
        const cloned = res.clone();
        const body = await cloned.json();
        code = body && body.code;
    } catch (_) {}

    if (code !== 'NO_TOKEN' && code !== 'TOKEN_EXPIRED') {
        return res; // 401 от другой причины (e.g. forbidden/role) — не refreshим
    }

    const refreshed = await _refresh();
    if (!refreshed) {
        // Cookie revoked/expired — на login
        if (typeof window !== 'undefined' && window.location) {
            window.location.replace('/login.html');
        }
        return res;
    }

    // Retry один раз
    return fetch(url, opts);
}
